// src/config/demoWaiver.js
//
// The demo-day liability waiver text, pinned by version.
//
// ⚠️ SOURCE OF TRUTH: /03-Legal/04-Templates/WAKA_Demo-Liability-Waiver_v1-DRAFT_2026-07-22.md
// This module renders that text for on-screen signing. The Cloud Function
// (functions/demo-waiver-text.js) carries an identical copy for PDF
// generation — ANY edit here must be mirrored there and MUST bump
// WAIVER_VERSION. The app stores a SHA-256 of the exact rendered text
// (waiverTextHash) with every signature, so drift is detectable.
//
// v1 approved by Nigel 2026-07-22 (public at /waiver). Attorney review still recommended before high-volume public events.
// (SPEC-2026-07-22-demo-checkout.md §10 — launch dependency.)

export const WAIVER_VERSION = 'v1-2026-07-22';

export const CONSENT_TEXT =
  'Yes — send me WAKA news, demo follow-ups and product updates. Unsubscribe anytime.';

// Section list drives both the on-screen scroll view and the PDF.
// Keep the numbered structure identical to the legal template.
export const WAIVER_SECTIONS = [
  {
    heading: 'RELEASE OF LIABILITY, WAIVER OF CLAIMS, EXPRESS ASSUMPTION OF RISK AND INDEMNITY AGREEMENT',
    body:
      'Equipment Demonstration — WAKA Inc.\n' +
      '4000 Brush Creek Road, Unit 21, Snowmass Village, CO 81615, USA\n\n' +
      'PLEASE READ CAREFULLY. THIS IS A LEGAL DOCUMENT THAT AFFECTS YOUR LEGAL RIGHTS, ' +
      'INCLUDING YOUR RIGHT TO SUE OR RECOVER DAMAGES. BY SIGNING, YOU GIVE UP IMPORTANT LEGAL RIGHTS.\n\n' +
      'In consideration of being permitted to use, ride, or otherwise participate in the demonstration of ' +
      'foilboards, hydrofoils, and related equipment (the "Equipment") provided by WAKA Inc., its parents, ' +
      'subsidiaries, affiliates, owners, directors, officers, employees, agents, contractors, volunteers, ' +
      'authorized dealers and demo partners, event hosts, and the owners or operators of the demo site ' +
      '(collectively, the "Released Parties"), I, the undersigned participant ("I" or "Participant"), agree as follows:',
  },
  {
    heading: '1. Acknowledgment of Inherent Risks',
    body:
      'I understand and acknowledge that wing foiling, foilboarding, hydrofoiling, and all related watersports ' +
      'are INHERENTLY DANGEROUS activities that involve significant risks of SERIOUS BODILY INJURY, PERMANENT ' +
      'DISABILITY, DROWNING, AND DEATH. These risks include, but are not limited to: the hydrofoil itself — a ' +
      'rigid mast and wings with thin, hard edges capable of causing deep lacerations, blunt-force trauma, and ' +
      'severe injury on contact, whether in a fall, a collision, or handling in and out of the water; falls onto ' +
      'the board, the foil, the water surface, or submerged objects, at speed or otherwise; collision with other ' +
      'riders, watercraft, swimmers, buoys, docks, rocks, or the bottom, including in shallow water; changing ' +
      'wind, weather, waves, tides, currents, water temperature, and vessel wakes; equipment failure or ' +
      'malfunction, including of the board, hinge system, foil, leash, or any safety gear; cold-water immersion, ' +
      'exhaustion, cramp, hypothermia, and drowning; my own errors of judgment or skill, and the actions or ' +
      'negligence of other people on the water; and conditions at the demo site, on land and in the water, ' +
      'whether known or unknown. I understand that these risks cannot be eliminated by instruction, supervision, ' +
      'or care taken by the Released Parties, and that no safety equipment (including helmets, impact vests, or ' +
      'flotation devices) can protect against all injury.',
  },
  {
    heading: '2. Voluntary Participation and Express Assumption of Risk',
    body:
      'I am participating in this demonstration voluntarily and of my own free will. I EXPRESSLY AND VOLUNTARILY ' +
      'ASSUME ALL RISK OF PERSONAL INJURY, ILLNESS, PERMANENT DISABILITY, DEATH, AND PROPERTY DAMAGE OR LOSS ' +
      'sustained while using the Equipment or participating in the demonstration, whether or not identified above, ' +
      'AND WHETHER OR NOT CAUSED BY THE NEGLIGENCE OF THE RELEASED PARTIES, to the maximum extent permitted by ' +
      'applicable law.',
  },
  {
    heading: '3. Release and Waiver of Claims',
    body:
      'To the maximum extent permitted by applicable law, I hereby RELEASE, WAIVE, AND FOREVER DISCHARGE the ' +
      'Released Parties from any and all liability, claims, demands, actions, and causes of action whatsoever ' +
      'arising out of or related to any loss, damage, injury, or death that I may sustain while using the ' +
      'Equipment or participating in the demonstration, INCLUDING CLAIMS ARISING FROM THE NEGLIGENCE OF THE ' +
      'RELEASED PARTIES, whether occurring on the water, on the beach, or anywhere at or around the demo site.',
  },
  {
    heading: '4. Covenant Not to Sue',
    body:
      'I agree that I WILL NOT SUE OR MAKE ANY CLAIM against the Released Parties for any loss, damage, injury, ' +
      'or death covered by this Agreement, and that this Agreement may be pleaded as a complete bar to any such claim.',
  },
  {
    heading: '5. Indemnification',
    body:
      'I agree to INDEMNIFY, DEFEND, AND HOLD HARMLESS the Released Parties from any and all claims, judgments, ' +
      'losses, and costs, including reasonable attorneys\' fees, arising out of my use of the Equipment or my ' +
      'participation in the demonstration, including claims brought by third parties arising from my actions.',
  },
  {
    heading: '6. Fitness, Swimming Ability, and Medical Consent',
    body:
      'I represent that: (a) I am a competent swimmer; (b) I am in good physical health and have no medical ' +
      'condition that would impair my ability to participate safely; (c) I am not under the influence of alcohol, ' +
      'drugs, or any impairing medication; and (d) I will stop and notify demo staff immediately if I feel unwell ' +
      'or unsafe. In the event of injury, I consent to emergency medical treatment, and I agree that I am ' +
      'responsible for the costs of any such treatment; I acknowledge that the Released Parties do not provide ' +
      'medical, health, or disability insurance covering my participation.',
  },
  {
    heading: '7. Equipment Use and Care',
    body:
      'I agree to: (a) use the Equipment only as instructed by demo staff and within my ability level; (b) wear ' +
      'the leash and any safety equipment provided or required by staff; (c) inspect the Equipment before use and ' +
      'refuse it if it appears damaged or unsafe; (d) remain within the demo area designated by staff; and ' +
      '(e) return the Equipment promptly when asked or at the end of my session.',
  },
  {
    heading: '8. Photograph for Record-Keeping',
    body:
      'I understand that WAKA may take a photograph of me (or, where required, of my identification) at ' +
      'check-out, used solely for equipment check-out records and the administration of this waiver, retained in ' +
      'accordance with WAKA\'s records practices, and not used for marketing without my separate consent.',
  },
  {
    heading: '9. Minors',
    body:
      'If the Participant is under 18 years of age (or a minor under the law of the place of the demonstration), ' +
      'the undersigned parent or legal guardian: (a) represents that they have legal authority to sign on the ' +
      'minor\'s behalf; (b) agrees to all terms of this Agreement on their own behalf and on behalf of the minor, ' +
      'to the maximum extent permitted by applicable law; and (c) agrees to indemnify the Released Parties for ' +
      'any claim brought by or on behalf of the minor arising from the minor\'s participation.',
  },
  {
    heading: '10. Electronic Signature',
    body:
      'I agree that my electronic signature on this Agreement (including a signature drawn on a touchscreen and ' +
      'my typed legal name) has the same legal force and effect as a handwritten signature, and I consent to ' +
      'receive a copy of this signed Agreement by email.',
  },
  {
    heading: '11. General Provisions',
    body:
      'Severability: if any provision of this Agreement is held invalid or unenforceable, the remaining ' +
      'provisions shall continue in full force and effect, and the invalid provision shall be enforced to the ' +
      'maximum extent permitted by law. Entire agreement: this Agreement is the entire agreement between me and ' +
      'the Released Parties concerning its subject matter and may not be modified orally. This Agreement applies ' +
      'to my participation in the demonstration event identified in the signing record, including any number of ' +
      'equipment sessions during that event.',
  },
  {
    heading: '12. Acknowledgment',
    body:
      'I HAVE READ THIS RELEASE OF LIABILITY, WAIVER OF CLAIMS, EXPRESS ASSUMPTION OF RISK AND INDEMNITY ' +
      'AGREEMENT. I FULLY UNDERSTAND ITS TERMS. I UNDERSTAND THAT I AM GIVING UP SUBSTANTIAL LEGAL RIGHTS, ' +
      'INCLUDING THE RIGHT TO SUE. I AM SIGNING FREELY AND VOLUNTARILY, WITHOUT ANY INDUCEMENT.',
  },
];

// Canonical plain-text rendering — the string that gets hashed. The Cloud
// Function reproduces this EXACTLY (same join rules) to verify the hash.
export const WAIVER_TEXT = WAIVER_SECTIONS
  .map((s) => `${s.heading}\n\n${s.body}`)
  .join('\n\n');

// SHA-256 hex of the waiver text (browser WebCrypto).
export async function computeWaiverHash(text = WAIVER_TEXT) {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
